// Signed in, two-step done, a company picked: then the app. Otherwise off to the right step.
import { useQueryClient } from "@tanstack/react-query";
import { Navigate } from "@tanstack/react-router";
import { useEffect, useSyncExternalStore } from "react";
import { api, ApiError, companyStore, setCompany } from "../../api/client";
import { type Landing, useCompanies, useLanding, useMe, useSignInLook } from "../../api/hooks";
import { useToast } from "../../components/overlays";
import { Button, Loading } from "../../components/parts";
import { SessionProvider } from "../session";
import { Shell } from "../Shell";
import { leavingForSso, markSignedOut } from "../sso";

/**
 * "https://app.thecleanersusa.com" and "https://steward.thecleanersusa.com" are one brand: the same domain past the first
 * part. Owner, Oct 31: on the old app. address the switcher dropped The Cleaners of New Orleans (its address is steward.).
 */
function sameBrandAddress(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  const domain = (u: string) => {
    try {
      const parts = new URL(u).hostname.split(".");
      return parts.length > 2 ? parts.slice(1).join(".") : null;
    } catch {
      return null;
    }
  };
  const d = domain(a);
  return d !== null && d === domain(b);
}

/** Signed in and two-step done, with a company picked: then the app. Otherwise off to the right step. */
export function SignedIn() {
  const me = useMe();
  const companies = useCompanies();
  const landing = useLanding();
  const look = useSignInLook();
  const qc = useQueryClient();
  const toast = useToast();
  const chosen = useSyncExternalStore(companyStore.subscribe, companyStore.get);
  const list = companies.data ?? [];
  const land = landing.data;
  // At a brand's own address (app.thecleanersusa.com) the app opens in that brand: a company saved from another brand is
  // set aside, so the switcher still reaches the rest but each visit starts here. Elsewhere the saved company comes first.
  const here = land?.kind === "brand" ? window.location.origin : null;
  // The saved company if the login still has it, else where this address starts them, else where they work (owners start
  // at the top of their tree).
  const company =
    list.find((c) => c.id === chosen && (!here || sameBrandAddress(c.linkUrl, here))) ??
    list.find((c) => c.id === (land?.kind === "no_access" ? null : land?.companyId)) ??
    list.find((c) => c.role !== "owner") ??
    list.find((c) => !list.some((x) => x.id === c.parentId)) ??
    list[0];
  const blocked = land?.kind === "no_access";
  useEffect(() => {
    if (company && company.id !== chosen && !blocked) setCompany(company.id);
  }, [company, chosen, blocked]);
  const switchHere = (id: string) => {
    setCompany(id);
    void qc.invalidateQueries();
  };
  // A company at another brand's address opens in a new tab there, signed in as this login (auth/handoff). The tab opens
  // on the click itself so the browser doesn't block it, and gets its address when the server answers. Each company's tab
  // has its own name, so switching again goes back to the tab already open instead of opening another.
  const switchTo = (id: string) => {
    const target = list.find((c) => c.id === id)?.linkUrl;
    if (__PREVIEW__ || !target || sameBrandAddress(target, window.location.origin)) {
      switchHere(id);
      return;
    }
    const tab = window.open("", `steward-${id}`);
    let fresh = true;
    try {
      // A new tab is a blank page we can read; one already on the brand's address is another site and throws.
      fresh = tab?.location.href === "about:blank";
    } catch {
      fresh = false;
    }
    if (tab && !fresh) {
      tab.focus();
      return;
    }
    if (tab) {
      // The opener stays: without it the browser forgets the tab's name and the next switch opens another. Both are ours.
      // The blank tab takes this page's background while the server answers, so it doesn't flash white.
      tab.document.title = "Steward";
      tab.document.body.style.cssText = `margin:0;background:${getComputedStyle(document.body).backgroundColor}`;
    }
    api<{ url: string | null }>("POST", "auth/handoff", { companyId: id }).then(
      ({ url }) => {
        if (!url) {
          tab?.close();
          switchHere(id);
        } else if (tab) tab.location.href = url;
        else window.location.assign(url);
      },
      (err: unknown) => {
        tab?.close();
        toast(err instanceof ApiError ? err.message : "That company didn't open. Try again.", { kind: "error" });
      },
    );
  };

  const err = me.error ?? companies.error;
  if (err instanceof ApiError) {
    if (err.code === "two_step_needed") return <Navigate to="/two-step" search={{ mode: "needed" }} />;
    if (err.code === "two_step_setup_needed") return <Navigate to="/two-step" search={{ mode: "setup" }} />;
    // Signed in at another brand's address? Head office's address knows, and brings this page back signed in (app/sso.ts).
    if (look.isLoading || leavingForSso(look.data?.sso)) return <Loading />;
    return <Navigate to="/sign-in" />;
  }
  if (me.isLoading || companies.isLoading || landing.isLoading || !me.data) return <Loading />;
  if (land?.kind === "no_access") return <NotHere landing={land} />;
  if (!company) {
    return (
      <div className="auth-main">
        <div className="auth-panel">
          <h1>No company yet</h1>
          <p className="lead">Your login isn't part of a company. Ask the owner to invite you.</p>
        </div>
      </div>
    );
  }
  return (
    <SessionProvider me={me.data} companies={list} company={company} onSwitch={switchTo}>
      <Shell />
    </SessionProvider>
  );
}

/** Signed in at a brand's address with no company there: a plain note and where their own companies are, nothing else. */
function NotHere({ landing }: { landing: Extract<Landing, { kind: "no_access" }> }) {
  const signOut = async () => {
    markSignedOut();
    await api("POST", "auth/sign-out").catch(() => null);
    window.location.assign("/sign-in");
  };
  return (
    <div className="auth-main">
      <div className="auth-panel">
        <h1>This is {landing.brand}</h1>
        <p className="lead">
          Your login isn't part of {landing.brand}. Open your own company below, or ask the {landing.brand} owner to invite you.
        </p>
        {landing.elsewhere.map((e) => (
          <a key={e.url} className="btn primary full" href={e.url}>
            Open {e.name}
          </a>
        ))}
        <Button type="tertiary" onClick={() => void signOut()}>
          Sign out
        </Button>
      </div>
    </div>
  );
}
